import { Box, Card, Grid, IconButton } from '@mui/material';
import axios from 'axios';
import './Global.scss';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Refresh } from '@mui/icons-material';
import { Chart as ChartJS } from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import Masonry from '@mui/lab/Masonry';
import { PexipInstance } from '../../models/PexipInstance';
import { Licence } from '../../models/Licence';
import { AuthHeader } from '../../utils/helpers/auth-headers';
import ChartPie from '../../components/charts/ChartPie';
import { register } from '../../components/charts/charts-data-config';
import { Alarm } from '../../models/Alarm';
import { Worker } from '../../models/Worker';

interface PexipInstanceGlobal extends PexipInstance {
  meetingCount: number;
  participantCount: number;
  licences: Licence[];
  videoQualityChartData: any;
  protocolsChartData: any;
  overviewStats: any;
  alarms: Alarm[];
  workers: Worker[];
}

function Global({ pexipInstances }: any) {
  ChartJS.register(register);
  const { t } = useTranslation();
  const [hasInstances, setHasInstances] = useState(false);
  const [globalInstances, setGlobalInstances] = useState(pexipInstances);
  const navigate = useNavigate();

  const setCharts = (instance: PexipInstanceGlobal) => {
    if (instance.overviewStats) {
      const protocolsData = instance.overviewStats.protocol;
      const labelsProtocol = Object.keys(protocolsData).sort();
      const dataProtocol = [];
      for (const key of labelsProtocol) {
        if (protocolsData[key]) {
          dataProtocol.push(protocolsData[key]);
        }
      }
      for (const i in labelsProtocol) {
        if (labelsProtocol[i]) {
          labelsProtocol[i] = `${labelsProtocol[i]} (${dataProtocol[i]})`;
        }
      }
      instance.protocolsChartData = {
        displaySubtitle: false,
        displayTitle: false,
        title: t('InstanceOverview.chartTitle.protocols'),
        data: dataProtocol,
        labels: labelsProtocol,
      };
      
      const qualityData = instance.overviewStats.quality;
      const labelsQuality = Object.keys(qualityData).sort();
      const dataQuality = [];
      for (const key of labelsQuality) {
        if (qualityData[key]) {
          dataQuality.push(qualityData[key]);
        }
      }
      for (const i in labelsQuality) {
        if (labelsQuality[i]) {
          labelsQuality[i] = `${labelsQuality[i]} (${dataQuality[i]})`;
        }
      }
      instance.videoQualityChartData = {
        displaySubtitle: false,
        displayTitle: false,
        title: t('InstanceOverview.chartTitle.videoQuality'),
        data: dataQuality,
        labels: labelsQuality,
        quality: true
      };
    }
    setGlobalInstances(globalInstances.map((i: PexipInstanceGlobal) => (i._id !== instance._id ? i : instance)));
  };
  
  const getInstanceStats = async (instance: PexipInstanceGlobal) => {
    const statsData = await axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instance._id}/liveStats`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
    instance.meetingCount = statsData?.data.data.meetingCount;
    instance.participantCount = statsData?.data.data.participantCount;
    instance.overviewStats = statsData?.data.data;
    setCharts(instance);
    setGlobalInstances(globalInstances.map((i: PexipInstanceGlobal) => (i._id !== instance._id ? i : instance)));      
  };

  const getInstanceLicences = async (instance: PexipInstanceGlobal) => {
    const licencesData = await axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instance._id}/licences`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
    instance.licences = licencesData?.data.data;
    setGlobalInstances(globalInstances.map((i: PexipInstanceGlobal) => (i._id !== instance._id ? i : instance)));
  };

  const getInstanceAlarms = async (instance: PexipInstanceGlobal) => {
    const alarmsData = await axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instance._id}/alarms`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
    instance.alarms = alarmsData?.data.data;
    for (const worker of instance.workers) {
      worker.alarms = [];
      for (const alarm of instance.alarms) {
        if (alarm.node === worker.address) {
          worker.alarms.push(alarm);
        }
      }
    }
    setGlobalInstances(globalInstances.map((i: PexipInstanceGlobal) => (i._id !== instance._id ? i : instance)));
  };

  const getInstanceWorkers = async (instance: PexipInstanceGlobal) => {
    const workersData = await axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instance._id}/workers`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
        }
        console.error(error);
      });
    instance.workers = workersData?.data.data;
    setGlobalInstances(globalInstances.map((i: PexipInstanceGlobal) => (i._id !== instance._id ? i : instance)));
    getInstanceAlarms(instance);
  };

  const getInstanceData = (instances: PexipInstanceGlobal[]) => {
    for (const instance of instances) {
      getInstanceLicences(instance);
      getInstanceStats(instance);
      getInstanceWorkers(instance);
    }
  };

  useEffect(() => {
    setHasInstances(false);
    if (pexipInstances && pexipInstances.length > 0) {
      setHasInstances(true);
      const pollingInterval = setInterval(() => {
        getInstanceData(pexipInstances);
      }, 10000);
      getInstanceData(pexipInstances);
      return () => {
        clearInterval(pollingInterval);
      };
    }
    return false as any;
  }, []);

  const calc80 = (current: number, total: number) => {
    if (((current * 100) / total) >= 80) {
      return true;
    } 
    return false;
  };

  const hasAlarms = (instance: PexipInstanceGlobal) => {
    if (instance?.alarms?.length > 0) {
      return true;
    }
    return false;
  };

  const isEmptyObject = (obj: any) => Object.keys(obj).length === 0; 

  const getColorAlert = (instance: PexipInstanceGlobal) => {
    if (instance?.alarms?.length > 0) {
      if (instance.alarms.filter((a: Alarm) => a.level === 'error').length > 0) {
        return 'is-invalid';
      }
      return 'is-warning';
    }
    return '';
  };

  const getClassNamesWorker = (worker: Worker) => {
    let classes = 'pie-load animate pointer';
    if (worker?.alarms?.length > 0) {
      if (worker.alarms.filter((a: Alarm) => a.level === 'error').length > 0) {
        classes += ' is-alarm-error';
      } else {
        classes += ' is-alarm-warning';
      }
    }
    return classes;
  };

  return (
    <>
      {!hasInstances && <div>{ t('Global.hasNoInstances') }</div> }
      { hasInstances && (
        <div>
          <div className="category-container">
            <Box sx={{ float: 'right', display: 'inline-flex', alignItems: 'center' }}>
              <div>{ t('InstanceOverview.refresh') }: {`${new Date().toLocaleTimeString()}`}</div>
              <IconButton onClick={() => getInstanceData(pexipInstances)}>
                <Refresh />
              </IconButton>
            </Box>
          </div>
          <div className="global-overview">
            <Masonry columns={2} spacing={0}>
              { globalInstances.map((pexipInstance: PexipInstanceGlobal) => (
                <div key={pexipInstance._id}>
                  <Card className="category-container pointer" aria-hidden="true" onClick={() => navigate(`/instance/${pexipInstance._id}/overview`)}>
                    <div className="card-title">{pexipInstance.name} {hasAlarms(pexipInstance) && <FontAwesomeIcon className={getColorAlert(pexipInstance)} icon={faWarning} />}</div>
                    <Grid container columns={12} direction="row" justifyContent="center" alignItems="center">
                      <Grid item xs={4}>
                        <div><strong>{ t('InstanceOverview.participantCount') }:&nbsp;</strong>{pexipInstance.participantCount}</div>
                        <div><strong>{ t('InstanceOverview.meetingCount') }:&nbsp;</strong>{pexipInstance.meetingCount}</div>
                        <br />
                        { pexipInstance?.licences?.length > 0 && (
                          <div>
                            {pexipInstance.licences.map((licence: Licence, i: number) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <div key={i}>
                                <div><strong>{ t('InstanceOverview.audioCount') }:&nbsp;</strong><span className={calc80(licence.audioCount, licence.audioTotal) ? 'reach-limits' : ''}>{licence.audioCount} / {licence.audioTotal}</span></div>
                                <div><strong>{ t('InstanceOverview.portCount') }:&nbsp;</strong><span className={calc80(licence.portCount, licence.portTotal) ? 'reach-limits' : ''}>{licence.portCount} / {licence.portTotal}</span></div>
                                <div><strong>{ t('InstanceOverview.vmrCount') }:&nbsp;</strong><span className={calc80(licence.vmrCount, licence.vmrTotal) ? 'reach-limits' : ''}>{licence.vmrCount} / {licence.vmrTotal}</span></div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Grid>
                      <Grid item xs={4}>
                        {pexipInstance.videoQualityChartData && !isEmptyObject(pexipInstance.overviewStats.quality) ? (
                          <div className="global-pie"><small>{t('InstanceOverview.chartTitle.videoQuality')}</small><ChartPie chartData={pexipInstance.videoQualityChartData} legendPlacement="top" cardWidth="300" cardHeight="200" chartHeight="200" /></div>
                        ) : <div className="global-pie"><small>{t('InstanceOverview.chartTitle.noDataVideoQuality')}</small></div>}
                      </Grid>
                      <Grid item xs={4}>
                        {pexipInstance.protocolsChartData && !isEmptyObject(pexipInstance.overviewStats.protocol) ? (
                          <div className="global-pie"><small>{t('InstanceOverview.chartTitle.protocols')}</small><ChartPie chartData={pexipInstance.protocolsChartData} legendPlacement="top" cardWidth="300" cardHeight="200" chartHeight="200" /></div>
                        ) : <div className="global-pie"><small>{t('InstanceOverview.chartTitle.noDataProtocols')}</small></div>}
                      </Grid>
                    </Grid>
                    { pexipInstance?.workers?.length > 0 && (
                      <div>
                        <Grid container columns={12}>
                          <Grid item xs={6}>
                            <div className="nodes-type"><strong><small>{ t('InstanceOverview.ConferencingNodes') }</small></strong></div>
                            <Grid container alignItems="center" justifyContent="center" className="border-nodes">
                              {pexipInstance.workers.map((worker: Worker) => (
                                <div key={`${worker.id}_conferencing`}>
                                  {worker.nodeType === 'CONFERENCING' && (
                                    <div className={worker?.alarms?.length > 0 ? 'worker-load-elements breath' : 'worker-load-elements'}>
                                      <div className={getClassNamesWorker(worker)} style={{ ['--p' as any]: worker.mediaLoad, ['--c' as any]: '#BD3156' }}>{worker.mediaLoad}%</div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </Grid>
                          </Grid>
                          <Grid item xs={6}>
                            <div className="nodes-type"><strong><small>{ t('InstanceOverview.ProxyingNodes') }</small></strong></div>
                            <Grid container alignItems="center" justifyContent="center">
                              {pexipInstance.workers.map((worker: Worker) => (
                                <div key={`${worker.id}_proxying`}>
                                  {worker.nodeType === 'PROXYING' && (
                                    <div className={worker?.alarms?.length > 0 ? 'worker-load-elements breath' : 'worker-load-elements'}>
                                      <div className={getClassNamesWorker(worker)} style={{ ['--p' as any]: worker.mediaLoad, ['--c' as any]: '#BD3156' }}>{worker.mediaLoad}%</div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </Grid>
                          </Grid>
                        </Grid>
                      </div>
                    )}
                  </Card>
                </div>
              ))}
            </Masonry> 
          </div>
        </div>
      )}
    </>
  );
}

export default Global;
